import type { ToolDefinition, ToolType } from "../types";

const COMPRESS_PDF_TOOL_TYPE = {
  label: "Free PDF Tool",
  icon: "FileText",
} as const satisfies ToolType;

const compressPdfTool: ToolDefinition = {
  slug: "compress-pdf",
  name: "Compress PDF File Online Free | Reduce PDF File Size",
  title: "Free Online PDF Compressor",
  description:
    "Reduce PDF file size online free without losing quality. The best free PDF compressor for large files, scanned pages, and email attachments — no account required.",
  longDescription:
    "Shrink your PDF file size instantly without sacrificing readability. Upload your document, select your optimization level, and download a compressed PDF ready for email, web uploads, or archiving. Works on mobile, completely free, no sign-up needed.",
  toolType: {
    label: "PDF Compressor",
    icon: "FileDown",
  },
  heroBadges: [
    "100% Free",
    "No Account Required",
    "No Watermark",
    "High Quality Output",
    "Works on Mobile",
    "Secure Processing",
  ],
  category: "PDF Tools",
  categorySlug: "pdf-tools",
  href: "/tools/compress-pdf",
  api: "/api/v1/pdf/optimize",
  keywords: [
    "snappy pdf compress tool",
    "how to reduce document size",
    "change file mb for email",
    "convert large folio to small",
    "minimize portfolio weight fast",
    "decrease scanned pages easily",
    "compress pdf high quality",
    "large file pdf compressor",
    "pdf optimizer online",
    "best way to compress pdf for archiving",
  ],
  pageContent: {
    sections: {
      steps: {
        title: "How to Compress a PDF File Online Free",
        description:
          "Follow these simple steps to reduce PDF file size in seconds — no software, no account, no watermark.",
      },
      benefits: {
        title: "Why Use Our Free PDF Compressor",
        description:
          "Fast, secure, and built for real-world file size problems — from oversized email attachments to large scanned documents.",
      },
      faqs: {
        title: "Frequently Asked Questions",
        description:
          "Answers to common questions about how to reduce PDF file size online, preserve quality, and handle already-compressed or scanned files.",
      },
    },
    intro: {
      title: "Shrink PDF File Size Online — Free, Fast, and High Quality",
      paragraphs: [
        "Need to reduce PDF file size online free and get on with your day? Whether you are trying to change file MB for email, meet a university portal upload limit, or shrink a large scanned document for archiving, our free PDF compressor handles it in seconds. No software to install, no account to create — just upload, compress, and download.",
        "Our PDF optimizer online works by intelligently targeting the elements inside your document that consume the most space: high-resolution embedded images, unused fonts, and hidden structural bloat. The result is a smaller, highly portable file that keeps its text crisp and its layout intact. It is the best free online PDF compressor for anyone dealing with oversized files, whether you are on a desktop, Android, or iPhone.",
      ],
      note: "Compressing a PDF permanently alters its internal structure. Always keep a backup of your original high-resolution file before optimizing.",
    },
    introBlocks: [
      {
        kind: "text",
        title: "What Actually Happens When You Compress a PDF File?",
        level: "h2",
        paragraphs: [
          "Many users want to reduce PDF size without losing quality, and the good news is that for most documents, you can do exactly that. PDF compression works by targeting specific elements: it downsamples high-resolution raster images (JPEGs, PNGs embedded in the document), removes unused embedded fonts, strips hidden metadata, and cleans up internal structural inefficiencies. Text is almost always vector-based inside a PDF, meaning it takes up very little space and is never affected by compression — your words stay perfectly sharp.",
          "If you are still asking 'why is my PDF still too big for email attachment?' after compressing once, the most common cause is a document filled with raw, high-resolution photographs or pages scanned at a very high DPI. Selecting the deeper optimization mode engages more aggressive image downsampling and garbage collection, which can reduce PDF file size dramatically — often below 500 KB even for image-heavy files. For documents that consist almost entirely of text, size reductions will be modest since there is little image data to remove.",
        ],
        note: "If your file is already under 1 MB and text-only, compression may not reduce it significantly — the tool works best on files between 2 MB and 50 MB with embedded images or scanned content.",
      },
      {
        kind: "table",
        tables: [
          {
            title: "Standard vs Deep Compression — Which Should You Choose?",
            description:
              "Understanding the two optimization paths helps you pick the right setting for your file type and target size.",
            columns: [
              "Mode",
              "Primary Action",
              "Best Used For",
              "Typical Result",
            ],
            rows: [
              {
                Mode: "Standard",
                "Primary Action":
                  "Balanced DPI reduction, preserves high visual fidelity",
                "Best Used For":
                  "Reports, resumes, presentations, mixed text and image files",
                "Typical Result":
                  "20–60% size reduction with excellent quality",
              },
              {
                Mode: "Deep / Pro",
                "Primary Action":
                  "Aggressive image downsampling and garbage collection",
                "Best Used For":
                  "Large scanned books, image portfolios, files exceeding strict upload limits",
                "Typical Result":
                  "50–85% size reduction; ideal when you need to compress PDF to under 1 MB",
              },
            ],
            note: "Actual compression ratio depends on how the original PDF was created and how much image data it contains.",
            emphasisColumns: ["Mode"],
            monospaceColumns: [],
          },
        ],
      },
      {
        kind: "callouts",
        groups: [
          {
            title: "Tips for the Best Compression Results",
            layout: "list",
            iconColor: "primary",
            items: [
              {
                label: "Start With the Right File Size",
                icon: "Search",
                description:
                  "Files between 2 MB and 50 MB with embedded images or scanned pages see the greatest size reductions. Already-compressed or text-only PDFs may not shrink much further.",
                accent: "primary",
              },
              {
                label: "Scanned Documents Need Deep Mode",
                icon: "FileText",
                description:
                  "Scanned PDFs are essentially image files page by page. To compress scanned PDFs while preserving legibility, use the deeper optimization mode, which targets DPI at the page level.",
                accent: "primary",
              },
              {
                label: "Archiving vs. Sharing",
                icon: "Archive",
                description:
                  "For long-term archiving, Standard mode preserves the most visual fidelity. For sharing via email or WhatsApp where file size is critical, Deep mode gives you the smallest possible output.",
                accent: "primary",
              },
            ],
          },
        ],
      },
    ],
    callouts: [
      {
        title: "Your Privacy Is Protected",
        layout: "list",
        iconColor: "primary",
        items: [
          {
            label: "Secure File Processing",
            icon: "ShieldCheck",
            description:
              "Your files are processed securely for the sole purpose of optimization and are not stored permanently. The compressed output is delivered directly back to your device — no one else can access it.",
            accent: "primary",
          },
        ],
      },
    ],
    useCases: [
      {
        label: "Email Attachments",
        icon: "Mail",
        description:
          "Reduce PDF file size to clear the 20 MB or 25 MB attachment limits on Gmail, Outlook, and other email clients without compromising document quality.",
        accent: "primary",
      },
      {
        label: "University & Portal Uploads",
        icon: "GraduationCap",
        description:
          "Compress large academic submissions, scanned assignments, and research documents to meet strict portal size requirements — often as low as 500 KB.",
        accent: "primary",
      },
      {
        label: "Web & Document Archiving",
        icon: "Globe",
        description:
          "Prepare heavy PDF brochures, whitepapers, and scanned archives for fast website downloads or long-term storage without losing readability.",
        accent: "primary",
      },
      {
        label: "Mobile Sharing",
        icon: "Smartphone",
        description:
          "Shrink PDFs for WhatsApp, Telegram, or mobile uploads directly from your Android or iPhone browser — no app download needed.",
        accent: "primary",
      },
    ],
    faqs: [
      {
        question:
          "How do I compress a PDF file online free without losing quality?",
        answer:
          "Upload your PDF to our free compressor and select Standard mode. This setting balances image downsampling with visual fidelity, so text stays razor-sharp and images remain clear. Since text in a standard PDF is vector-based, it is never degraded by compression — only embedded raster images are adjusted. For most documents you will see a significant size reduction with no perceptible quality loss.",
      },
      {
        question:
          "Why is my PDF still too big for an email attachment after compression?",
        answer:
          "This usually means your document contains very high-resolution photographs or was scanned at an extremely high DPI. Try the deeper compression mode, which engages more aggressive image downsampling and internal garbage collection. Most email clients cap attachments at 20 MB or 25 MB — our PDF optimizer online can bring the vast majority of documents well under that threshold. If the file is still too large, consider splitting it into sections first.",
      },
      {
        question:
          "What is the best free PDF compressor online in 2026 without a watermark?",
        answer:
          "Our tool is completely free, requires no account, and never adds watermarks to your output. The downloaded file is your document as it was — just smaller. It works directly in your browser on desktop, Android, and iPhone, making it a practical everyday solution for anyone needing to reduce PDF file size without installing software or paying for a subscription.",
      },
      {
        question: "How do I compress scanned PDFs while preserving legibility?",
        answer:
          "Scanned pages are essentially images saved page by page inside a PDF container. To compress them without making text unreadable, use the deeper optimization mode, which targets the image DPI for each page intelligently. The goal is to lower resolution just enough to slash the file size while keeping the text recognizable. For documents scanned at 300 DPI or higher, you can usually reduce PDF size substantially and still keep the content perfectly usable.",
      },
      {
        question:
          "What is the best way to compress PDF documents for archiving?",
        answer:
          "For archiving, Standard mode is the better choice. It reduces file size meaningfully while preserving the highest possible visual fidelity — important when you need documents to remain readable and professional years from now. If storage space is extremely tight, Deep mode will shrink files further but at the cost of some image sharpness. Always keep an uncompressed master copy before optimizing any document intended for long-term archiving.",
      },
      {
        question:
          "How can I reduce PDF file size to 500 KB for a university upload?",
        answer:
          "Select the deepest compression setting available in the tool. This mode applies aggressive image downsampling and removes all unnecessary internal data, which can bring even large image-heavy documents down to 500 KB or below. If your document is a text-only PDF, the savings will be smaller since there is little image data to remove. For scanned academic submissions, Deep mode almost always delivers the target size.",
      },
      {
        question:
          "How do I shrink a PDF on Android or iPhone without downloading an app?",
        answer:
          "Because our compressor is entirely web-based, it runs perfectly in any modern mobile browser. Open the tool on your Android or iPhone, tap the upload area to select your file from local storage or cloud, choose your compression level, and save the result directly to your device. No app installation, no account, and no file size limit beyond practical browser constraints.",
      },
      {
        question: "Can I compress a PDF that has already been compressed?",
        answer:
          "You can try, but results will be limited. A PDF that has already been optimized has had most of its redundant data removed. Running it through a compressor again will find very little left to shrink, and you risk marginal quality loss with minimal size benefit. If your file is still too large after a first pass, the best approach is to use a higher compression mode, reduce the number of pages, or remove high-resolution images before re-exporting from the original application.",
      },
      {
        question:
          "Does compressing a PDF remove hyperlinks, form fields, or bookmarks?",
        answer:
          "No. The optimization process targets image data, embedded fonts, and structural inefficiencies — not interactive elements. Your hyperlinks, fillable form fields, bookmarks, and internal document navigation remain fully intact in the downloaded output. The file will behave exactly as before, just with a smaller footprint.",
      },
      {
        question: "Can I compress a password-protected PDF online?",
        answer:
          "Not directly. The tool needs access to the document's internal structure to optimize it, and encryption blocks that access. You will need to remove the password locally first — most PDF readers let you do this if you know the password — then upload the unlocked file for compression. Once you have the optimized output, you can reapply password protection using your preferred PDF editor.",
      },
    ],
    benefits: [
      {
        icon: "Zap",
        title: "Fast Size Reduction",
        description:
          "Reduce PDF file size online in seconds. Our optimizer processes your document quickly, stripping image bloat and hidden data so you get a smaller file without a long wait.",
      },
      {
        icon: "Image",
        title: "High Quality Output",
        description:
          "Compress PDF without blurry text or degraded layouts. The tool targets image data intelligently while leaving vector text completely untouched — your document stays professional.",
      },
      {
        icon: "Mail",
        title: "Built for Email and Uploads",
        description:
          "Change file MB for email in one step. Clear attachment size limits on Gmail and Outlook, meet university portal caps, or prep files for web downloads without back-and-forth.",
      },
      {
        icon: "Archive",
        title: "Great for Archiving",
        description:
          "Compress PDF documents for long-term archiving without sacrificing readability. Standard mode preserves fidelity while meaningfully cutting storage footprint.",
      },
      {
        icon: "ShieldCheck",
        title: "Private and Secure",
        description:
          "Files are processed securely and not stored permanently. Your compressed PDF is delivered directly to your device — no third-party access, no permanent server copies.",
      },
      {
        icon: "Smartphone",
        title: "Works on Any Device",
        description:
          "A fully web-based tool that runs in your browser on desktop, Android, or iPhone. Shrink a PDF file size on mobile without downloading a single app.",
      },
    ],
    steps: [
      {
        number: "01",
        title: "Upload Your PDF",
        description:
          "Click the upload area or drag your PDF onto the interface to load it into the compressor. Confirm the file has been successfully added before continuing.",
      },
      {
        number: "02",
        title: "Choose Your Optimization Level",
        description:
          "Select the compression preset that fits your needs. Standard mode balances quality and size reduction; Deep mode delivers maximum shrinkage for large image-heavy or scanned files.",
      },
      {
        number: "03",
        title: "Compress the File",
        description:
          "Click the compress button and wait a moment while the optimizer analyzes your document, adjusts image DPI, and removes unnecessary internal data.",
      },
      {
        number: "04",
        title: "Download the Compressed PDF",
        description:
          "Save the reduced file to your device. The output is automatically named 'optimized_' followed by your original filename. Reset the tool to compress another document.",
      },
    ],
  },
};

export default compressPdfTool;
export { compressPdfTool };

// import type { ToolDefinition, ToolType } from "../types";

// const COMPRESS_PDF_TOOL_TYPE = {
//   label: "Free PDF Tool",
//   icon: "FileText",
// } as const satisfies ToolType;

// const compressPdfTool: ToolDefinition = {
//   slug: "compress-pdf",
//   name: "Compress PDF Online | Reduce PDF File Size Free",
//   title: "Online PDF Compressor",
//   description:
//     "Reduce PDF file size quickly without losing quality. Compress large PDF documents for email attachments easily and securely in your browser.",
//   longDescription:
//     "Quickly shrink your PDF file size without sacrificing readability. Upload your document, choose your optimization level, and download a compressed PDF ready for email attachments, web uploads, or archiving.",
//   toolType: {
//     label: "PDF Compressor",
//     icon: "FileDown",
//   },
//   heroBadges: [
//     "100% Free",
//     "No Account Required",
//     "Fast Processing",
//     "Maintains Quality",
//     "Mobile Friendly",
//   ],
//   category: "PDF Tools",
//   categorySlug: "pdf-tools",
//   href: "/tools/compress-pdf",
//   api: "/api/v1/pdf/optimize",
//   keywords: [
//     "snappy pdf compress tool",
//     "how to reduce document size",
//     "change file mb for email",
//     "convert large folio to small",
//     "minimize portfolio weight fast",
//     "decrease scanned pages easily",
//   ],
//   pageContent: {
//     sections: {
//       steps: {
//         title: "How to Compress a PDF File",
//         description:
//           "Follow these simple steps to reduce the size of your document quickly and securely.",
//       },
//       benefits: {
//         title: "Why Use Our PDF Compressor",
//         description:
//           "Enjoy a fast, secure, and intuitive way to shrink your large documents without dealing with complicated software.",
//       },
//       faqs: {
//         title: "Frequently Asked Questions",
//         description:
//           "Find answers to common questions about reducing PDF sizes, preserving quality, and handling large files.",
//       },
//     },
//     intro: {
//       title: "Shrink PDF Files for Faster Sharing",
//       paragraphs: [
//         "When you need to share important documents, oversized files can quickly become a frustrating barrier. Whether you are trying to submit a university assignment, upload a document to a web portal, or simply change file mb for email attachments, strict size limitations often block your progress. That is where our snappy pdf compress tool comes in. We provide a streamlined, user-friendly interface to decrease the size of PDF files in seconds without complicated software.",
//         "By analyzing the internal structure of your document, our compressor intelligently reduces bloat. It handles everything from overly large embedded images to unnecessary meta-information that inflates the document's footprint. The result is a lighter, highly portable file that retains the visual fidelity you need for professional or personal use. You can easily convert large folio to small, manageable sizes and share them instantly.",
//       ],
//       note: "Optimizing a PDF permanently alters its internal structure. We always recommend keeping a backup of your original high-resolution file.",
//     },
//     introBlocks: [
//       {
//         kind: "text",
//         title: "Understanding What PDF Compression Actually Does",
//         level: "h2",
//         paragraphs: [
//           "Many users wonder how to reduce document size without permanently destroying the layout or clarity of their files. PDF compression is a highly technical process that works by targeting specific elements within the document that consume the most space. Typically, this involves downsampling high-resolution images, removing unused embedded fonts, stripping out hidden metadata, and standardizing color profiles. Our tool offers an intuitive interface that handles these complex adjustments automatically, ensuring you decrease scanned pages easily without needing a degree in graphic design.",
//           "If you are dealing with a document that is still too large, you might ask: why is my pdf still too big for email attachment? Often, this happens when a document is filled with raw, high-resolution photographs or scanned at an extremely high DPI. By utilizing deeper optimization settings, you can easily reduce pdf file size to 500kb or less, depending on the original content structure. We strive to provide the best free pdf compressor 2026 has to offer, giving you robust control over how aggressively you want to shrink your files while preserving essential readable text and interactive elements.",
//         ],
//         note: "Documents that consist almost entirely of text are already highly optimized and may not see significant size reductions.",
//       },
//       {
//         kind: "table",
//         tables: [
//           {
//             title: "Optimization Modes Explained",
//             description:
//               "Understanding the difference between standard and deeper compression paths to pick the best option for your file.",
//             columns: ["Mode", "Primary Action", "Best Used For"],
//             rows: [
//               {
//                 Mode: "Standard",
//                 "Primary Action":
//                   "Balances DPI reduction with high visual fidelity",
//                 "Best Used For":
//                   "General reports, resumes, and text documents with some images",
//               },
//               {
//                 Mode: "Deep / Pro",
//                 "Primary Action":
//                   "Aggressive image downsampling and garbage collection",
//                 "Best Used For":
//                   "Large scanned books, dense image portfolios, and strict upload limits",
//               },
//             ],
//             note: "Actual size reduction depends heavily on how the original PDF was generated.",
//             emphasisColumns: ["Mode"],
//             monospaceColumns: [],
//           },
//         ],
//       },
//       {
//         kind: "callouts",
//         groups: [
//           {
//             title: "Pro Tips for Best Results",
//             layout: "list",
//             iconColor: "primary",
//             items: [
//               {
//                 label: "Check Before You Compress",
//                 icon: "Search",
//                 description:
//                   "If your file is already under 1MB, it may not shrink much further. The tool works best on files between 5MB and 50MB.",
//                 accent: "primary",
//               },
//               {
//                 label: "Text vs. Images",
//                 icon: "Type",
//                 description:
//                   "Text is vector-based and very small. Large files are almost always caused by unoptimized images or poorly scanned pages.",
//                 accent: "primary",
//               },
//             ],
//           },
//         ],
//       },
//     ],
//     callouts: [
//       {
//         title: "Secure Processing Notice",
//         layout: "list",
//         iconColor: "primary",
//         items: [
//           {
//             label: "Privacy First",
//             icon: "Lock",
//             description:
//               "We prioritize your privacy. Your files are processed securely for the sole purpose of optimization, and the resulting file is made available exclusively to you for download.",
//             accent: "primary",
//           },
//         ],
//       },
//     ],
//     useCases: [
//       {
//         label: "Email Attachments",
//         icon: "Mail",
//         description:
//           "Shrink documents to bypass strict 20MB or 25MB attachment limits on popular email clients.",
//         accent: "primary",
//       },
//       {
//         label: "University Uploads",
//         icon: "GraduationCap",
//         description:
//           "Reduce large academic submissions and scanned assignments to fit within strict portal size constraints.",
//         accent: "primary",
//       },
//       {
//         label: "Web Optimization",
//         icon: "Globe",
//         description:
//           "Prepare heavy PDF brochures and whitepapers for fast website downloading to improve user experience.",
//         accent: "primary",
//       },
//     ],
//     faqs: [
//       {
//         question: "How do I compress a PDF for an email attachment?",
//         answer:
//           "To change file mb for email, simply upload your document to our tool. Choose the appropriate optimization mode based on your required file size limit, and click compress. Most email providers limit attachments to 20MB or 25MB. Our compressor easily shrinks large files below this threshold, ensuring your email sends without bouncing back.",
//       },
//       {
//         question: "Why is my PDF file size not decreasing after compression?",
//         answer:
//           "If you are trying to figure out the best way to compress a pdf that is already compressed, you might find diminishing returns. Documents that consist entirely of pure text or have already been run through a strong optimizer lack the redundant data needed to shrink further. Compression works best on files with unoptimized images, embedded fonts, and hidden structural bloat.",
//       },
//       {
//         question: "What is the best free pdf compressor 2026 no watermark?",
//         answer:
//           "Our snappy pdf compress tool is completely free to use and never adds intrusive watermarks to your documents. We believe in providing a seamless utility that respects your original content. The final downloaded file will look professional and remain unaltered by any branding or logos.",
//       },
//       {
//         question:
//           "How can I reduce a PDF's size without making the text blurry?",
//         answer:
//           "Text inside a standard PDF is usually vector-based, meaning it scales infinitely without losing quality and takes up very little space. Compression primarily targets embedded raster images (like JPEGs or PNGs). Therefore, you can reduce pdf size without losing image quality significantly, and your text will remain perfectly crisp and readable.",
//       },
//       {
//         question:
//           "Does compressing a PDF remove interactive links or form fields?",
//         answer:
//           "No, optimizing the file size focuses on structural data, image DPI, and background coding inefficiencies. Your hyperlinks, fillable form fields, bookmarks, and basic document navigation will remain fully functional in the downloaded 'optimized_' version of your document.",
//       },
//       {
//         question:
//           "How to shrink a pdf file size on android without downloading apps?",
//         answer:
//           "Because our tool is entirely web-based, it works perfectly as a mobile solution. You can access the compress pdf online free 2026 no sign up platform directly from your Android or iOS browser. Just tap the upload area to select a file from your mobile device's storage, process it, and save the condensed version directly to your phone.",
//       },
//       {
//         question: "Can I compress a secure password protected pdf online?",
//         answer:
//           "Currently, you must remove the password or encryption from your PDF before uploading it to the optimizer. The tool needs to access the internal structure of the document to clean up metadata and downsample images. Once unlocked, you can upload, compress, and then reapply your security settings locally.",
//       },
//       {
//         question: "How to hyper compress pdf to 1mb online for free?",
//         answer:
//           "For users needing extreme file size reduction, such as needing to reduce pdf file size to 500kb for a university upload, we recommend selecting the highest compression mode available in the tool's interface. This deeper mode engages garbage collection and drops the target DPI significantly, drastically reducing the size of image-heavy documents.",
//       },
//     ],
//     benefits: [
//       {
//         icon: "Zap",
//         title: "Instant Size Reduction",
//         description:
//           "Minimize portfolio weight fast. Our optimization engine quickly processes your uploaded document, stripping away unnecessary bloat and redundant data so you can get back to work.",
//       },
//       {
//         icon: "Image",
//         title: "Maintains Visual Quality",
//         description:
//           "Reduce PDF size without losing image quality or rendering text unreadable. The tool intelligently balances file size reduction with visual fidelity, ensuring your documents look highly professional.",
//       },
//       {
//         icon: "Mail",
//         title: "Perfect for Email",
//         description:
//           "Change file mb for email seamlessly. Avoid frustrating bounce-backs and attachment format limits by converting oversized documents into lightweight files that send instantly.",
//       },
//       {
//         icon: "ShieldCheck",
//         title: "Secure Processing",
//         description:
//           "We respect your privacy and data. Files are securely processed for optimization and are not stored permanently. The generated optimized output is delivered directly back to your device.",
//       },
//     ],
//     steps: [
//       {
//         number: "01",
//         title: "Upload the PDF",
//         description:
//           "Add a PDF file to the compressor by clicking the upload area or dragging your document onto the interface. Confirm the file is successfully loaded into the tool.",
//       },
//       {
//         number: "02",
//         title: "Choose optimization mode",
//         description:
//           "Pick the preset or optimization option shown in the current UI. Select between standard compression for a balanced result or deeper compression for maximum size reduction.",
//       },
//       {
//         number: "03",
//         title: "Compress the file",
//         description:
//           "Start the optimization process by clicking the compress button. Wait a few moments for the tool to analyze the document structure, adjust DPI, and clean up hidden data.",
//       },
//       {
//         number: "04",
//         title: "Download the optimized PDF",
//         description:
//           "Once processing is complete, save the reduced file size version to your device. The tool automatically prefixes it with 'optimized_'. Reset the tool if you want to compress another document.",
//       },
//     ],
//   },
// };

// export default compressPdfTool;
// export { compressPdfTool };
